import { Box, Flex, Text } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import {
  Button,
  ButtonText,
  FormFeedback,
  PasswordInput,
  useToast
} from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import axiosInstance from 'src/api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthRealm } from '../../routes/AuthRealmAccessControl/AuthRealmContext';
import {
  getVerifyExpireLink,
  useResetPasswordMutation
} from 'src/api/signUpMutation';
import { useEffect, useState } from 'react';
import SessionUpdateModal from 'src/components/User/SessionUpdateModal';
import { BrandLogo } from '../SignUpInviteSent';
import { passwordRegex } from 'src/components/FormField/common/CustomFormFIeldPasswordInput';
import CopyrightFooterSection from 'src/components/User/CopyrightFooterSection';

export type ResetPasswordForm = {
  password: string;
  confirmPassword: string;
};

export function replaceRedirectUrl(
  url: string,
  newRedirectUri: string
): string {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('redirect_uri', newRedirectUri);
  return parsedUrl.toString();
}

export const useUserDetailsMutation = () => {
  const mutation = useMutation({
    mutationFn: ({ payload, userId }: { userId: string; payload: any }) => {
      return axiosInstance
        .post<any>(`/users/registration-draft/${userId}`, {
          payload
        })
        .then(r => ({
          ...r.data,
          isSuccess: true as const
        }))
        .catch(e => ({
          message: e.response.data,
          isSuccess: false as const
        }));
    }
  });

  return mutation;
};

export const toastMessage = {
  title: 'Link Expired',
  description: 'This link has expired. Please request a new link'
};

const ResetPassword = () => {
  const { loginUrl, clientBrand } = useAuthRealm();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const toast = useToast();
  const navigate = useNavigate();

  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isLinkVerified, setIsLinkVerified] = useState(false);

  const resetPasswordMutation = useResetPasswordMutation();
  const methods = useForm<ResetPasswordForm>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const {
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
    register,
    watch
  } = methods;

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const handleVerifyAndShowForm = async () => {
    if (!token || isResettingPassword || isLinkVerified) {
      return;
    }

    setIsResettingPassword(true);

    try {
      const data = await getVerifyExpireLink(token);

      if (data?.isExpired) {
        toast({ variant: 'error', ...toastMessage });
        navigate(`/signin/forgotpassword`);
        return;
      }

      setIsLinkVerified(true);
    } catch {
      toast({ variant: 'error', ...toastMessage });
      navigate(`/signin/forgotpassword`);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    const values = getValues();

    const response = await resetPasswordMutation.mutateAsync({
      token,
      password: values.password
    });
    if (!response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Reset password link error',
        description: response?.message?.message
          ? response?.message?.message
          : response?.message?.error
          ? response?.message?.error
          : 'Internal Server Error',
        isClosable: true
      });
    } else {
      toast({
        variant: 'success',
        title: 'Success',
        description: response?.message
          ? response?.message
          : 'Internal Server Error',
        isClosable: true
      });
      setIsOpenConfirmationModal(true);
    }
  };

  const handleLogin = () => {
    const newLoginUrl = replaceRedirectUrl(loginUrl, window.location.origin);
    window.location.href = newLoginUrl;
  };

  useEffect(() => {
    if (!token) {
      navigate(`/signin/forgotpassword`);
      return;
    }

    if (confirmPassword) {
      trigger('confirmPassword');
    }
  }, [confirmPassword, navigate, password, token, trigger]);

  if (token && !isLinkVerified) {
    return (
      <Flex
        height="100%"
        flexGrow={2}
        flexBasis={0}
        minHeight={'100%'}
        direction="column"
      >
        <Flex
          flexDirection="column"
          gap={12}
          width={{ base: '95%', md: '80%' }}
          marginX={'auto'}
          flex={1}
        >
          <Flex justifyContent={'space-between'} alignItems={'end'}>
            <BrandLogo />
          </Flex>

          <Flex flex="1" alignItems="center" justifyContent="center" px={4}>
            <Box textAlign="center" maxW="640px" width="full">
              <Text textStyle="headingTitle">Reset Password</Text>
              <Text mt={2}>
                Please confirm you want to reset your password.
              </Text>
              <Button
                onClick={handleVerifyAndShowForm}
                my={8}
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Verifying...' : 'Reset Password'}
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex width="full" height="100vh" overflow="hidden">
      {/* Left side: dynamic content from nested routes */}
      <Flex flex="3" width={'full'} marginX={'auto'} overflowY={'hidden'}>
        <Flex
          height="100%"
          flexGrow={2}
          flexBasis={0}
          minHeight={'100%'}
          direction="column"
        >
          <Flex
            flexDirection="column"
            gap={12}
            width={'80%'}
            marginX={'auto'}
            flex={1}
          >
            <SessionUpdateModal
              isIconHide={true}
              isOpen={isOpenConfirmationModal}
              onClose={() => {}}
              title="Password Reset Successful"
              message={
                <>
                  Your password has been changed successfully. <br />
                  Please log in with your new password to continue
                </>
              }
              actionButton={
                <Flex justifyContent={'flex-end'} gap={3} mt={3}>
                  <Button onClick={handleLogin}>Go to Login</Button>
                </Flex>
              }
            />
            <Flex justifyContent={'space-between'} alignItems={'end'}>
              <BrandLogo />
            </Flex>
            <Flex flexDirection={'column'} gap={4}>
              <Flex direction={'column'}>
                <Text textStyle="headingTitle">Reset Password</Text>
                <Text>Create new password to access your account.</Text>
                <FormProvider {...methods}>
                  <form
                    autoComplete="on"
                    onSubmit={handleSubmit(handleResetPassword)}
                  >
                    <Flex gap={4} direction="column" marginTop={4}>
                      <Flex direction={'column'} gap={3} flex={1}>
                        <FieldLabel
                          label="New Password"
                          subText="Your password must be at least 8 characters and include at least one uppercase letter, one lowercase letter and one number."
                          isRequired
                        />
                        <PasswordInput
                          placeholder="Set a password"
                          {...register('password', {
                            required: 'Please set a new password',
                            pattern: {
                              value: passwordRegex,
                              message:
                                'Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter and one number'
                            }
                          })}
                          isInvalid={!!errors.password}
                          autoComplete="new-password"
                        />
                        {errors.password && (
                          <FormFeedback variant="error">
                            {errors.password?.message}
                          </FormFeedback>
                        )}
                      </Flex>

                      <Flex direction={'column'} gap={3} flex={1}>
                        <FieldLabel
                          label="Confirm Password"
                          subText=""
                          isRequired
                        />
                        <PasswordInput
                          placeholder="Confirm your password"
                          {...register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: value => {
                              const newPassword = getValues('password');
                              if (
                                value &&
                                newPassword &&
                                value !== newPassword
                              ) {
                                return 'The passwords you entered do not match. Please try again';
                              }
                              return true;
                            }
                          })}
                          isInvalid={!!errors.confirmPassword}
                          autoComplete="new-password"
                        />
                        {errors.confirmPassword && (
                          <FormFeedback variant="error">
                            {errors.confirmPassword?.message}
                          </FormFeedback>
                        )}
                      </Flex>

                      <Button
                        type="submit"
                        my={3}
                        variant="primary"
                        disabled={resetPasswordMutation.isLoading}
                      >
                        Reset Password
                      </Button>
                    </Flex>
                  </form>
                  <Flex justifyContent={'center'} gap={1}>
                    <Text textStyle={'headingItem'}>
                      {' '}
                      Did you remember your password?
                    </Text>
                    <ButtonText onClick={handleLogin}>Sign In</ButtonText>
                  </Flex>
                </FormProvider>
              </Flex>
            </Flex>
          </Flex>
          <CopyrightFooterSection />
        </Flex>
      </Flex>
      {/* Right side: fixed banner */}
      <Flex
        height="full"
        flexGrow={3}
        flexBasis={0}
        display={{ base: 'none', md: 'flex' }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100vh'
          }}
          className="image-container"
        >
          <img
            src={clientBrand.signupBackgroundImage}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'fill'
            }}
            alt="Description"
          />
        </div>
      </Flex>
    </Flex>
  );
};

export default ResetPassword;
